
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription, PricingPlan } from "@/hooks/useSubscription";
import PricingCards from "./pricing/PricingCards";
import { DEFAULT_PRICING_FEATURES } from "./pricing/constants";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

export default function PricingTable() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { 
    plans, 
    isCurrentPlan, 
    formatPrice, 
    handleSubscribe, 
    loading,
    isTestMode,
    error
  } = useSubscription();

  // Group plans by type for display
  const freePlan = plans.find(plan => plan.is_active && plan.type === "free");
  const monthlyPlan = plans.find(plan => plan.is_active && plan.type === "monthly");
  const yearlyPlan = plans.find(plan => plan.is_active && plan.type === "yearly");

  // All available plans for display
  const availablePlans = [freePlan, monthlyPlan, yearlyPlan].filter(Boolean) as PricingPlan[];

  return (
    <div className="w-full max-w-6xl mx-auto">
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {isTestMode && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded">
          <p className="font-medium">Stripe Test Mode Active</p>
          <p className="text-sm">Use test card: 4242 4242 4242 4242 with any future expiry date, CVC and ZIP</p>
          <p className="text-sm mt-2">
            <strong>Important:</strong> If you're seeing price ID errors, ensure your Stripe product and price IDs 
            match the mode (test/live) of your Stripe API key.
          </p>
        </div>
      )}
      
      {availablePlans.length === 0 && (
        <div className="text-center p-8 bg-muted rounded-lg">
          <p className="text-lg text-muted-foreground">No pricing plans available at this time.</p>
        </div>
      )}
      
      {availablePlans.length > 0 && (
        <PricingCards
          plans={availablePlans}
          featuresList={DEFAULT_PRICING_FEATURES}
          formatPrice={formatPrice}
          user={user}
          isCurrentPlan={isCurrentPlan}
          handleSubscribe={(planId) => {
            try {
              handleSubscribe(planId);
            } catch (error) {
              console.error("Failed to initiate subscription:", error);
              toast.error("Failed to initiate subscription. Please try again later.");
            }
          }}
          loading={loading}
        />
      )}
    </div>
  );
}
